import { create } from "zustand";
import { persist } from "zustand/middleware";
import { clearAuthSessionHistory } from "./authSessionGuard";
import { clearAllSeenGuides } from "./roleGuide";
import { queryClient } from "./queryClient";
import type { Profile } from "./types";

export type AuthUser = {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  usertype: string;
  username?: string;
  organisationId?: number | string | null;
  organisationName?: string | null;
  organisationTier?: string | null;
  /** Cascade role(s) from organisationmembership (prime_lead / prime_member /
   * sub_lead / sub_member). Used to label team members distinctly from org leads;
   * usertype alone can't tell them apart. Absent for non-cascade users. */
  organisationRoles?: string[] | null;
};

type AuthState = {
  user: AuthUser | null;
  token: string | null;
  login: (user: AuthUser, token: string) => void;
  logout: () => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      login: (user, token) => set({ user, token }),
      logout: () => {
        clearAuthSessionHistory();
        clearAllSeenGuides();
        // Nothing else clears the TanStack Query cache on a login/logout
        // boundary - a query that succeeded (even against the wrong
        // workspace, or under a previous user/demo session in the same
        // tab) would otherwise keep being served as "fresh" data for its
        // staleTime into whatever session comes next. Covers the explicit
        // sign-out button AND the 401-triggered auto-logout (AuthGuard's
        // onUnauthorized calls this same action).
        queryClient.clear();
        set({ user: null, token: null });
      },
    }),
    {
      name: "il-auth",
      // The JWT itself never touches localStorage - only the in-memory store
      // holds it for this tab's session. The server already issues a genuine
      // httpOnly jwt_authorisation cookie at login (server/routers/authentication.js)
      // that survives a refresh on its own; AuthGuard's GET /auth/me call
      // re-derives both `user` and a fresh in-memory `token` from that cookie
      // when there's no persisted `user` to short-circuit the check. Persisting
      // `user` here is just a UX convenience (skips the loading spinner on
      // refresh) - it carries no bearer credential.
      partialize: (state) => ({ user: state.user }),
    },
  ),
);

const USERTYPE_MAP: Record<
  string,
  { group: Profile["group"]; landing: string; role: string; permissions: string[] }
> = {
  STRATUS: {
    group: "oversight",
    landing: "/stratus",
    role: "Oversight operator",
    permissions: ["manage", "approve", "view-all"],
  },
  PHILANTHRIFY_OPS: {
    group: "oversight",
    landing: "/stratus",
    role: "Platform admin",
    permissions: ["manage", "approve", "view-all", "ai-upload"],
  },
  FUNDER: {
    group: "funder",
    landing: "/funder",
    role: "Funder",
    permissions: ["view-disbursements", "approve-disbursements"],
  },
  PRIME_CONTRACTOR: {
    group: "contractor",
    landing: "/workspace/uganda-fire-stations",
    role: "Prime contractor",
    // Mirrors seed profile prime-james: can lead projects and approve asks
    permissions: ["contractor.lead", "ask.approve"],
  },
  SUBCONTRACTOR: {
    group: "contractor",
    landing: "/workspace/uganda-fire-stations",
    role: "Subcontractor",
    // Mirrors seed profile sub-naome: approval is scoped to own sub-projects only
    permissions: ["subcontractor.lead", "ask.approve.scoped"],
  },
  REPORTER: {
    group: "reporter",
    landing: "/reporter",
    role: "Reporter",
    permissions: ["raise-ask", "submit-evidence"],
  },
};

// Cascade roles that are org *members*, not leads — labelled "Team member"
// rather than the org persona ("Prime contractor" / "Subcontractor"). The
// workspace and permissions are unchanged; this only affects the display label.
const MEMBER_CASCADE_ROLES = new Set(["prime_member", "sub_member"]);

export function buildProfileFromUser(user: AuthUser): Profile {
  const cfg = USERTYPE_MAP[user.usertype] ?? {
    group: "other" as Profile["group"],
    landing: "/stratus",
    role: user.usertype,
    permissions: [],
  };
  const isContractorMember =
    (user.usertype === "PRIME_CONTRACTOR" || user.usertype === "SUBCONTRACTOR") &&
    Array.isArray(user.organisationRoles) &&
    user.organisationRoles.some((r) => MEMBER_CASCADE_ROLES.has(r));
  const roleLabel = isContractorMember ? "Team member" : cfg.role;
  const initials =
    `${user.firstname?.[0] ?? ""}${user.lastname?.[0] ?? ""}`.toUpperCase();
  return {
    id: String(user.id),
    displayName: `${user.firstname} ${user.lastname}`,
    role: roleLabel,
    organisation: user.organisationName ?? "",
    avatarInitials: initials,
    landing: cfg.landing,
    permissions: cfg.permissions,
    group: cfg.group,
    personId: String(user.id),
    organisationId:
      user.organisationId != null ? String(user.organisationId) : "",
  };
}
